import { useState, useEffect } from 'react'
import { PlusIcon, MagnifyingGlassIcon, XMarkIcon, TrashIcon, DocumentDuplicateIcon, PencilIcon, EyeIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { norm } from '../utils/normalize'
import { useToast } from '../contexts/ToastContext'
import SortableHeader, { useSort } from '../components/SortableHeader'

const API_BASE_URL = '/api/v1/dev'

interface Unit {
  id: number
  name: string
  type: 'mass' | 'volume' | 'count'
  to_g_factor: number | null
}

interface Ingredient {
  id: number
  name: string
  type: string
  default_unit_id?: number
  default_unit_name: string
  allergens: Array<{ id: number; name: string }>
  density?: { id: number; density_g_per_ml: string } | null
  unit_weights?: Array<{ id: number; weight_g_per_unit: string }>
}

interface RecipeItem {
  id?: number
  ingredient_id: number
  ingredient_name?: string
  quantity: string
  unit_id: number
  unit_name?: string
  order: number
  notes: string
}

interface Recipe {
  id: number
  code: string
  name: string
  alias: string
  type: 'elaborated_ingredient' | 'finished_product'
  finished_product_id?: number
  finished_product_name?: string
  final_unit: string
  final_quantity_expected: string
  yield_percentage: string
  conservation?: 'refrigerado' | 'congelado' | 'ambiente'
  usage_text?: string
  items_count: number
  estimated_cost: number
}

interface RecipeFormData {
  alias: string
  name: string
  type: 'elaborated_ingredient' | 'finished_product'
  finished_product_id: number
  final_unit_id: number
  final_quantity_expected: string
  shelf_life_days: number
  conservation: 'refrigerado' | 'congelado' | 'ambiente'
  usage_text: string
  notes: string
  items: RecipeItem[]
  instructionSteps: InstructionStep[]
}

interface InstructionStep {
  order: number
  text: string
}

export default function Recipes() {
  const toast = useToast()
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [units, setUnits] = useState<Unit[]>([])
  const [ingredients, setIngredients] = useState<Ingredient[]>([])
  const [allIngredients, setAllIngredients] = useState<Ingredient[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingRecipe, setViewingRecipe] = useState<any>(null)
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState<RecipeFormData>({
    alias: '',
    name: '',
    type: 'finished_product',
    finished_product_id: 0,
    final_unit_id: 0,
    final_quantity_expected: '',
    shelf_life_days: 7,
    conservation: 'refrigerado',
    usage_text: '',
    notes: '',
    items: [],
    instructionSteps: []
  })

  const [currentItem, setCurrentItem] = useState<RecipeItem>({
    ingredient_id: 0,
    quantity: '',
    unit_id: 0,
    order: 1,
    notes: ''
  })

  const fetchRecipes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()
      if (response.ok && data.success) {
        setRecipes(data.data)
      } else {
        setError('Error cargando recetas')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const fetchUnits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/units`)
      const data = await response.json()
      if (response.ok) {
        setUnits(data.data)
      }
    } catch (error) {
      console.error('Error fetching units:', error)
    }
  }

  const fetchIngredients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingredients`)
      const data = await response.json()
      if (response.ok) {
        // Store all ingredients for product selection
        setAllIngredients(data.data)
        // Filter out packaging and cleaning products for recipe items
        const filteredIngredients = data.data.filter((ing: Ingredient) =>
          ing.type !== 'packaging' && ing.type !== 'limpieza'
        )
        setIngredients(filteredIngredients)
      }
    } catch (error) {
      console.error('Error fetching ingredients:', error)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchRecipes(),
        fetchUnits(),
        fetchIngredients()
      ])
      setLoading(false)
    }
    loadData()
  }, [])

  // Note: Yield percentage is calculated on the backend after saving
  // because it requires unit conversion to grams which is done server-side

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (formData.items.length === 0) {
      toast.error('Debes agregar al menos un ingrediente a la receta')
      return
    }

    if (!formData.finished_product_id) {
      toast.error('Debes seleccionar un producto/ingrediente a elaborar')
      return
    }

    const body = {
      alias: formData.alias,
      name: formData.name,
      type: formData.type,
      finished_product_id: formData.finished_product_id,
      final_unit_id: formData.final_unit_id,
      final_quantity_expected: parseFloat(formData.final_quantity_expected),
      // yield_percentage is calculated on the backend
      shelf_life_days: formData.shelf_life_days,
      conservation: formData.conservation,
      usage_text: formData.usage_text,
      notes: formData.notes || null,
      // Secuencia combinada (ingredientes + instrucciones) ordenada por order;
      // se reasigna order contiguo y se separa para el backend.
      ...(() => {
        const combined = [
          ...formData.items.map(it => ({ kind: 'ingredient' as const, order: it.order, it })),
          ...formData.instructionSteps.filter(s => s.text.trim()).map(s => ({ kind: 'instruction' as const, order: s.order, s })),
        ].sort((a, b) => a.order - b.order)
        const items: any[] = []
        const instruction_steps: any[] = []
        combined.forEach((el, idx) => {
          const order = idx + 1
          if (el.kind === 'ingredient') {
            items.push({
              ingredient_id: el.it.ingredient_id,
              quantity: parseFloat(el.it.quantity),
              unit_id: el.it.unit_id,
              stage: 'preparation',
              order,
              notes: el.it.notes || null,
            })
          } else {
            instruction_steps.push({ order, text: el.s.text.trim() })
          }
        })
        return { items, instruction_steps }
      })()
    }

    try {
      const url = editingRecipe
        ? `${API_BASE_URL}/recipes/${editingRecipe.id}`
        : `${API_BASE_URL}/recipes`

      const response = await fetch(url, {
        method: editingRecipe ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await fetchRecipes()
        handleCloseModal()
        toast.success(editingRecipe ? 'Receta actualizada' : 'Receta creada')
      } else {
        toast.error(data.message || 'Error guardando receta')
      }
    } catch (error) {
      toast.error('Error de conexión')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Está seguro de que desea eliminar esta receta?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setRecipes(recipes.filter(recipe => recipe.id !== id))
        toast.success('Receta eliminada')
      } else {
        toast.error(data.message || 'Error eliminando receta')
      }
    } catch (error) {
      toast.error('Error de conexión')
    }
  }

  const handleView = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${id}`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()
      if (response.ok && data.success) {
        setViewingRecipe(data.data)
        setShowViewModal(true)
      } else {
        setError('Error cargando receta')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const handleEdit = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${id}`, {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()
      if (response.ok && data.success) {
        const recipe = data.data.recipe
        const items = data.data.items

        setEditingRecipe(recipe)
        setFormData({
          alias: recipe.alias || '',
          name: recipe.name,
          type: recipe.type,
          finished_product_id: recipe.finished_product_id || 0,
          final_unit_id: recipe.final_unit_id,
          final_quantity_expected: parseFloat(recipe.final_quantity_expected).toFixed(2),
          shelf_life_days: recipe.shelf_life_days || 7,
          conservation: (recipe.conservation as any) || 'refrigerado',
          usage_text: recipe.usage_text || '',
          notes: recipe.notes || '',
          items: items.map((item: any) => ({
            ingredient_id: item.ingredient.id,
            ingredient_name: item.ingredient.name,
            quantity: parseFloat(item.quantity).toFixed(2),
            unit_id: units.find(u => u.name === item.unit)?.id || 0,
            unit_name: item.unit,
            order: item.order,
            notes: item.notes || ''
          })),
          instructionSteps: (data.data.instruction_steps || []).map((s: any) => ({
            order: s.order, text: s.text
          }))
        })
        setShowAddModal(true)
      } else {
        setError('Error cargando receta')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const handleDuplicate = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/recipes/${id}/duplicate`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        await fetchRecipes()
        // Show success message temporarily
        const tempSuccess = document.createElement('div')
        tempSuccess.className = 'fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-6 py-3 rounded-md shadow-lg z-50'
        tempSuccess.textContent = '✓ Receta duplicada exitosamente'
        document.body.appendChild(tempSuccess)
        setTimeout(() => {
          tempSuccess.remove()
        }, 3000)
      } else {
        setError(data.message || 'Error duplicando receta')
      }
    } catch (error) {
      setError('Error de conexión')
    }
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingRecipe(null)
    setFormData({
      alias: '',
      name: '',
      type: 'finished_product',
      finished_product_id: 0,
      final_unit_id: 0,
      final_quantity_expected: '',
      shelf_life_days: 7,
      conservation: 'refrigerado',
      usage_text: '',
      notes: '',
      items: [],
      instructionSteps: []
    })
    setCurrentItem({
      ingredient_id: 0,
      quantity: '',
      unit_id: 0,
      order: 1,
      notes: ''
    })
    setError('')
  }

  const handleAddItem = () => {
    if (!currentItem.ingredient_id || !currentItem.quantity || !currentItem.unit_id) {
      toast.error('Completa todos los campos del ingrediente')
      return
    }

    const ingredient = ingredients.find(i => i.id === currentItem.ingredient_id)
    const unit = units.find(u => u.id === currentItem.unit_id)

    const newItem: RecipeItem = {
      ...currentItem,
      ingredient_name: ingredient?.name,
      unit_name: unit?.name,
      order: nextOrder()
    }

    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    })

    setCurrentItem({
      ingredient_id: 0,
      quantity: '',
      unit_id: 0,
      order: 1,
      notes: ''
    })
    setError('')
  }

  // --- Secuencia combinada (ingredientes + instrucciones) ---
  const nextOrder = () =>
    Math.max(0, ...formData.items.map(i => i.order), ...formData.instructionSteps.map(s => s.order)) + 1

  // Lista ordenada de la secuencia para renderizar
  const sequence = (): Array<{ kind: 'ingredient'; order: number; item: RecipeItem } | { kind: 'instruction'; order: number; step: InstructionStep }> => {
    const els = [
      ...formData.items.map(item => ({ kind: 'ingredient' as const, order: item.order, item })),
      ...formData.instructionSteps.map(step => ({ kind: 'instruction' as const, order: step.order, step })),
    ]
    return els.sort((a, b) => a.order - b.order)
  }

  const addInstruction = () => {
    setFormData(fd => ({ ...fd, instructionSteps: [...fd.instructionSteps, { order: nextOrderOf(fd), text: '' }] }))
  }
  const nextOrderOf = (fd: RecipeFormData) =>
    Math.max(0, ...fd.items.map(i => i.order), ...fd.instructionSteps.map(s => s.order)) + 1

  const removeAt = (order: number) => {
    setFormData(fd => ({
      ...fd,
      items: fd.items.filter(i => i.order !== order),
      instructionSteps: fd.instructionSteps.filter(s => s.order !== order),
    }))
  }

  // Mueve el elemento (por order) una posición arriba/abajo intercambiando order con el vecino
  const moveAt = (order: number, dir: -1 | 1) => {
    const seq = sequence()
    const idx = seq.findIndex(e => e.order === order)
    const target = idx + dir
    if (idx < 0 || target < 0 || target >= seq.length) return
    const a = seq[idx].order
    const b = seq[target].order
    setFormData(fd => ({
      ...fd,
      items: fd.items.map(i => i.order === a ? { ...i, order: b } : i.order === b ? { ...i, order: a } : i),
      instructionSteps: fd.instructionSteps.map(s => s.order === a ? { ...s, order: b } : s.order === b ? { ...s, order: a } : s),
    }))
  }

  const updateItemAt = (order: number, patch: Partial<RecipeItem>) => {
    setFormData(fd => ({
      ...fd,
      items: fd.items.map(i => i.order === order ? { ...i, ...patch } : i),
    }))
  }
  const updateInstructionAt = (order: number, text: string) => {
    setFormData(fd => ({
      ...fd,
      instructionSteps: fd.instructionSteps.map(s => s.order === order ? { ...s, text } : s),
    }))
  }

  // Unidades a ofrecer para un ingrediente en la receta: las de la misma familia
  // que su unidad por defecto + sus unidades de trabajo configuradas (equivalencias).
  const unitsForIngredient = (ingredientId: number): Unit[] => {
    const ing = ingredients.find(i => i.id === ingredientId)
    if (!ing) return units
    const defUnit = units.find(u => u.id === ing.default_unit_id)
      || units.find(u => u.name === ing.default_unit_name)
    const family = defUnit?.type
    const workIds = new Set((ing.unit_weights || []).map(w => w.id)) // w.id = unit_id
    return units.filter(u =>
      (family && u.type === family) || workIds.has(u.id) || u.id === defUnit?.id
    )
  }

  // Volume unit name -> ml factor (matches backend UnitConverter)
  const volumeToMl: Record<string, number> = {
    mililitro: 1, ml: 1, centilitro: 10, cl: 10, decilitro: 100, dl: 100, litro: 1000, l: 1000
  }

  // Equivalencia explícita del ingrediente para esta unidad (TBSP/TSP/ml…)
  const explicitWeight = (unit: Unit, ingredient: Ingredient): number | null => {
    const uw = ingredient.unit_weights?.find(w => w.id === unit.id)
    const g = uw ? parseFloat(String(uw.weight_g_per_unit)) : NaN
    return !isNaN(g) && g > 0 ? g : null
  }

  const toGrams = (qty: number, unit: Unit, ingredient: Ingredient): number | null => {
    const explicit = explicitWeight(unit, ingredient)
    if (explicit !== null) return qty * explicit
    if (unit.type === 'mass') {
      const factor = parseFloat(String(unit.to_g_factor))
      return isNaN(factor) || factor === 0 ? null : qty * factor
    }
    if (unit.type === 'volume') {
      const mlFactor = volumeToMl[unit.name.toLowerCase()]
      const density = ingredient.density ? parseFloat(String(ingredient.density.density_g_per_ml)) : NaN
      if (!mlFactor || isNaN(density)) return null
      return qty * mlFactor * density
    }
    if (unit.type === 'count') {
      const uw = ingredient.unit_weights?.find(w => w.id === unit.id)
      if (!uw) return null
      return qty * parseFloat(String(uw.weight_g_per_unit))
    }
    return null
  }

  const fromGrams = (grams: number, unit: Unit, ingredient: Ingredient): number | null => {
    const explicit = explicitWeight(unit, ingredient)
    if (explicit !== null) return grams / explicit
    if (unit.type === 'mass') {
      const factor = parseFloat(String(unit.to_g_factor))
      return isNaN(factor) || factor === 0 ? null : grams / factor
    }
    if (unit.type === 'volume') {
      const mlFactor = volumeToMl[unit.name.toLowerCase()]
      const density = ingredient.density ? parseFloat(String(ingredient.density.density_g_per_ml)) : NaN
      if (!mlFactor || isNaN(density)) return null
      return (grams / density) / mlFactor
    }
    return null
  }

  const calculateExpectedQuantity = () => {
    if (formData.items.length === 0) {
      toast.error('Agrega ingredientes antes de calcular')
      return
    }
    let totalGrams = 0
    for (const item of formData.items) {
      const unit = units.find(u => u.id === item.unit_id)
      // Use allIngredients to find full ingredient data (with density/unit_weights)
      const ingredient = allIngredients.find(i => i.id === item.ingredient_id)
      if (!unit || !ingredient) {
        toast.error(`Datos no encontrados para "${item.ingredient_name}"`)
        return
      }
      const grams = toGrams(parseFloat(item.quantity), unit, ingredient)
      if (grams === null) {
        toast.error(`No se puede convertir "${item.ingredient_name}" (${unit.name}) a gramos. Revisa que tenga densidad o peso por unidad configurado.`)
        return
      }
      totalGrams += grams
    }
    // Convert total grams to final unit
    const finalUnit = units.find(u => u.id === formData.final_unit_id)
    const finishedProduct = allIngredients.find(i => i.id === formData.finished_product_id)
    if (!finalUnit || !finishedProduct) {
      toast.error('Selecciona un producto y unidad final primero')
      return
    }
    const result = fromGrams(totalGrams, finalUnit, finishedProduct)
    if (result === null) {
      toast.error('No se puede convertir a la unidad final. Revisa la densidad del producto.')
      return
    }
    setFormData({ ...formData, final_quantity_expected: result.toFixed(3) })
    toast.success('Cantidad calculada a partir de los ingredientes')
  }

  const [currentPage, setCurrentPage] = useState(1)
  const { sortField, sortDirection, handleSort, sortData } = useSort<Recipe>('name')
  const perPage = 15

  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = norm(recipe.name).includes(norm(searchTerm)) ||
                         norm(recipe.code).includes(norm(searchTerm)) ||
                         norm(recipe.alias || '').includes(norm(searchTerm))
    const matchesType = typeFilter === 'all' || recipe.type === typeFilter
    return matchesSearch && matchesType
  })

  const sortedRecipes = sortData(filteredRecipes)
  const totalPages = Math.ceil(filteredRecipes.length / perPage)
  const paginatedRecipes = sortedRecipes.slice((currentPage - 1) * perPage, currentPage * perPage)

  useEffect(() => { setCurrentPage(1) }, [searchTerm, typeFilter])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Recetas</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestión de recetas para ingredientes elaborados y productos terminados
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Nueva Receta
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre o código..."
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">Todos los tipos</option>
              <option value="finished_product">Productos Terminados</option>
              <option value="elaborated_ingredient">Ingredientes Elaborados</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recipes Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Alias" field="alias" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Producto/Ingrediente" field="name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Tipo" field="type" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Cantidad Esperada" field="final_quantity_expected" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Estado" field="is_active" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ingredientes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRecipes.map((recipe) => (
                <tr key={recipe.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {recipe.alias || '-'}
                    </div>
                    <div className="text-xs text-gray-400 font-mono">{recipe.code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {recipe.finished_product_name || recipe.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      recipe.type === 'finished_product'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {recipe.type === 'finished_product' ? 'Producto Terminado' : 'Ingrediente Elaborado'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseFloat(recipe.final_quantity_expected).toFixed(2)} {recipe.final_unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {parseFloat(recipe.yield_percentage).toFixed(0)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {recipe.items_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleView(recipe.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Ver detalles"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(recipe.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Editar"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(recipe.id)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Duplicar"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(recipe.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages} ({filteredRecipes.length} recetas)
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingRecipe ? 'Editar Receta' : 'Nueva Receta'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alias *
                  </label>
                  <input
                    type="text"
                    value={formData.alias}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Nombre corto o identificador de la receta"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Debe ser único por producto
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({
                      ...formData,
                      type: e.target.value as 'elaborated_ingredient' | 'finished_product',
                      finished_product_id: 0,
                      final_unit_id: 0,
                      name: ''
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="finished_product">Producto Terminado</option>
                    <option value="elaborated_ingredient">Ingrediente Elaborado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.type === 'finished_product' ? 'Producto a Elaborar *' : 'Ingrediente a Elaborar *'}
                  </label>
                  <select
                    value={formData.finished_product_id}
                    onChange={(e) => {
                      const productId = parseInt(e.target.value)
                      const selectedProduct = allIngredients.find(ing => ing.id === productId)
                      const unitId = selectedProduct ? units.find(u => u.name === selectedProduct.default_unit_name)?.id || 0 : 0
                      setFormData({
                        ...formData,
                        finished_product_id: productId,
                        final_unit_id: unitId,
                        name: selectedProduct?.name || ''
                      })
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value={0}>Seleccionar {formData.type === 'finished_product' ? 'producto' : 'ingrediente'}...</option>
                    {allIngredients
                      .filter(ing => formData.type === 'finished_product'
                        ? ing.type === 'finished_product'
                        : ing.type === 'elaborated')
                      .map(ingredient => (
                        <option key={ingredient.id} value={ingredient.id}>
                          {ingredient.name} ({ingredient.default_unit_name})
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    El nombre de la receta se tomará del ingrediente/producto seleccionado
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cantidad Esperada *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.001"
                      min="0.001"
                      value={formData.final_quantity_expected}
                      onChange={(e) => setFormData({ ...formData, final_quantity_expected: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                    <button
                      type="button"
                      onClick={calculateExpectedQuantity}
                      className="px-3 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-md whitespace-nowrap"
                      title="Calcular a partir de los ingredientes"
                    >
                      Calcular
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Suma de ingredientes en la unidad final. Puedes modificarla después.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidad Final
                  </label>
                  <input
                    type="text"
                    value={formData.finished_product_id
                      ? allIngredients.find(ing => ing.id === formData.finished_product_id)?.default_unit_name || 'Sin unidad'
                      : 'Selecciona un producto primero'}
                    disabled
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-700"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La unidad se define en el ingrediente/producto
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vida Útil (días) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.shelf_life_days}
                    onChange={(e) => setFormData({ ...formData, shelf_life_days: parseInt(e.target.value) || 7 })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Días de vida útil del producto una vez elaborado
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conservación *
                  </label>
                  <select
                    value={formData.conservation}
                    onChange={(e) => setFormData({ ...formData, conservation: e.target.value as RecipeFormData['conservation'] })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="refrigerado">Refrigerado</option>
                    <option value="congelado">Congelado</option>
                    <option value="ambiente">Ambiente</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Se incluye en la etiqueta del producto
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Uso
                  </label>
                  <input
                    type="text"
                    value={formData.usage_text}
                    onChange={(e) => setFormData({ ...formData, usage_text: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 placeholder:italic placeholder:text-gray-300"
                    placeholder="Ej: Descongelar 2 h antes de servir"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Opcional. Si se rellena, aparece al final de la etiqueta como "Uso: …"
                  </p>
                </div>
              </div>

              {/* Ingredients Section */}
              <div className="border-t pt-4">
                <h4 className="text-md font-medium text-gray-900 mb-4">Ingredientes e instrucciones</h4>

                {/* Añadir ingrediente */}
                <div className="bg-gray-50 p-4 rounded-md mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Ingrediente</label>
                      <select
                        value={currentItem.ingredient_id}
                        onChange={(e) => {
                          const ingredientId = parseInt(e.target.value)
                          const ingredient = ingredients.find(i => i.id === ingredientId)
                          setCurrentItem({
                            ...currentItem,
                            ingredient_id: ingredientId,
                            unit_id: ingredient ? units.find(u => u.name === ingredient.default_unit_name)?.id || 0 : 0
                          })
                        }}
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                      >
                        <option value={0}>Seleccionar...</option>
                        {ingredients.map(ingredient => (
                          <option key={ingredient.id} value={ingredient.id}>{ingredient.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
                      <input
                        type="number" step="0.001" min="0.001"
                        value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Unidad</label>
                      <select
                        value={currentItem.unit_id}
                        onChange={(e) => setCurrentItem({ ...currentItem, unit_id: parseInt(e.target.value) })}
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                      >
                        <option value={0}>Seleccionar...</option>
                        {unitsForIngredient(currentItem.ingredient_id).map(unit => (
                          <option key={unit.id} value={unit.id}>{unit.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button type="button" onClick={handleAddItem} className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-sm">
                        + Ingrediente
                      </button>
                    </div>
                    <div className="flex items-end">
                      <button type="button" onClick={addInstruction} className="w-full bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-md text-sm">
                        + Instrucción
                      </button>
                    </div>
                  </div>
                </div>

                {/* Secuencia: ingredientes e instrucciones intercalados, reordenables */}
                {sequence().length > 0 ? (
                  <div className="space-y-2">
                    {sequence().map((el, idx, arr) => (
                      <div
                        key={el.order}
                        className={`flex items-start gap-2 border rounded-md p-2 ${el.kind === 'instruction' ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}
                      >
                        {/* Flechas de orden */}
                        <div className="flex flex-col">
                          <button type="button" disabled={idx === 0} onClick={() => moveAt(el.order, -1)} className="text-gray-500 hover:text-gray-800 disabled:opacity-30 leading-none">▲</button>
                          <button type="button" disabled={idx === arr.length - 1} onClick={() => moveAt(el.order, 1)} className="text-gray-500 hover:text-gray-800 disabled:opacity-30 leading-none">▼</button>
                        </div>

                        {el.kind === 'ingredient' ? (
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                            {/* El ingrediente no se edita: para cambiarlo, eliminar y añadir otro */}
                            <span className="md:col-span-3 px-2 py-1.5 text-sm font-medium text-gray-900">
                              {el.item.ingredient_name || ingredients.find(i => i.id === el.item.ingredient_id)?.name || '—'}
                            </span>
                            <input
                              type="number" step="0.001" min="0.001"
                              value={el.item.quantity}
                              onChange={(e) => updateItemAt(el.order, { quantity: e.target.value })}
                              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            />
                            <select
                              value={el.item.unit_id}
                              onChange={(e) => updateItemAt(el.order, { unit_id: parseInt(e.target.value) })}
                              className="md:col-span-2 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                            >
                              <option value={0}>Unidad…</option>
                              {unitsForIngredient(el.item.ingredient_id).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                          </div>
                        ) : (
                          <div className="flex-1">
                            <span className="block text-[11px] font-semibold text-amber-700 mb-1">INSTRUCCIÓN</span>
                            <textarea
                              value={el.step.text}
                              onChange={(e) => updateInstructionAt(el.order, e.target.value)}
                              rows={2}
                              placeholder="Escribe el paso…"
                              className="w-full border border-amber-300 rounded-md px-2 py-1.5 text-sm bg-white"
                            />
                          </div>
                        )}

                        <button type="button" onClick={() => removeAt(el.order)} className="text-red-600 hover:text-red-900 mt-1" title="Quitar">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Añade ingredientes e instrucciones; podrás ordenarlos con las flechas.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  {editingRecipe ? 'Actualizar' : 'Crear'} Receta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingRecipe && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Detalles de Receta
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => window.open(`${API_BASE_URL}/recipes/${viewingRecipe.recipe.id}/datasheet`, '_blank')}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  Ficha técnica
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Recipe Header Information */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Código</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">{viewingRecipe.recipe.code}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">
                      {viewingRecipe.recipe.type === 'finished_product' ? 'Producto a Elaborar' : 'Ingrediente a Elaborar'}
                    </label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {viewingRecipe.recipe.finished_product_name || viewingRecipe.recipe.name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Tipo</label>
                    <p className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        viewingRecipe.recipe.type === 'finished_product'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {viewingRecipe.recipe.type === 'finished_product' ? 'Producto Terminado' : 'Ingrediente Elaborado'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Cantidad Esperada</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {parseFloat(viewingRecipe.recipe.final_quantity_expected).toFixed(2)} {viewingRecipe.recipe.final_unit}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Rendimiento</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {parseFloat(viewingRecipe.recipe.yield_percentage).toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Vida Útil</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {viewingRecipe.recipe.shelf_life_days || 7} días
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase">Coste Estimado</label>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {viewingRecipe.cost_summary.total_cost ? `€${parseFloat(viewingRecipe.cost_summary.total_cost).toFixed(2)}` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              {viewingRecipe.instruction_steps && viewingRecipe.instruction_steps.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instrucciones</label>
                  <ol className="list-decimal list-inside space-y-1 bg-white border border-gray-300 rounded-md px-4 py-3 text-sm text-gray-900">
                    {viewingRecipe.instruction_steps.map((s: any) => (
                      <li key={s.id} className="whitespace-pre-wrap">{s.text}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Notes */}
              {viewingRecipe.recipe.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
                  <div className="bg-white border border-gray-300 rounded-md px-4 py-3 text-sm text-gray-900 whitespace-pre-wrap">
                    {viewingRecipe.recipe.notes}
                  </div>
                </div>
              )}

              {/* Ingredients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ingredientes</label>
                <div className="border rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ingrediente</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">En Gramos</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notas</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {viewingRecipe.items.map((item: any, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.ingredient.name}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {parseFloat(item.quantity).toFixed(3)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.unit}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.quantity_in_grams ? parseFloat(item.quantity_in_grams).toFixed(2) : 'N/A'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            {item.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Allergens */}
              {viewingRecipe.recipe.allergens && viewingRecipe.recipe.allergens.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Alérgenos</label>
                  <div className="flex flex-wrap gap-2">
                    {viewingRecipe.recipe.allergens.map((allergen: any, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                      >
                        {allergen.name || allergen}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
